import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import gsap from 'gsap';

import backgroundTracks from './backgroundTracks';
import { subscribeManifestoBackground } from '../lib/manifestoBackground';
import { subscribeHomeSection, getHomeSection } from '../lib/homeSection';
import { subscribeInteractionAudio } from '../lib/interactionAudio';
import { prefersReducedMotion } from '../lib/motion';
import { AUDIO_MODES, DEFAULT_AUDIO_MODE } from './audioModes';
import {
    startSiteAudio,
    setSiteAudioMuted,
    setSiteAudioSection,
    siteAudioHover,
    siteAudioSectionTransition,
    siteAudioHoldStart,
    siteAudioHoldProgress,
    siteAudioHoldRelease,
    siteAudioHoldComplete,
    siteAudioProjectPreviewChange,
    siteAudioProjectCanvasTransition,
    siteAudioProjectOpen,
    siteAudioProjectClose,
    siteAudioLabNavigate,
} from './audioController';

// Active audio backend for the whole site (audioModes.js). In GENERATIVE mode
// this provider drives the Tone.js engine via audioController; in MP3 mode it
// keeps owning the legacy <audio> element below; in OFF mode nothing plays.
const AUDIO_MODE = DEFAULT_AUDIO_MODE;
const IS_GENERATIVE = AUDIO_MODE === AUDIO_MODES.GENERATIVE;
const IS_MP3 = AUDIO_MODE === AUDIO_MODES.MP3;

// Home publishes its active section as an index (see lib/homeSection.js &
// Home.js `['home','projects','lab','info']`). Map those to generative audio
// section presets (generativeAudioConfig.js): projects→work, info→about.
const HOME_INDEX_TO_AUDIO_STATE = ['home', 'work', 'lab', 'about'];

const DEFAULT_VOLUME = 0.5;

// While the hero manifesto plays, the background music ducks out so the spoken voice is clear. We lower
// the VOLUME only (never pause) — so playback resumes seamlessly when the manifesto ends, and if the user
// had the music off the (inaudible) volume change leaves it off. Tunable: raise MANIFESTO_DUCK_VOLUME for
// a faint musical bed under the voice instead of full silence.
const MANIFESTO_DUCK_VOLUME = 0.0;
const MANIFESTO_DUCK_FADE = 0.8; // seconds — smooth duck-out / restore fade

const BackgroundAudioContext = createContext(null);

/**
 * Picks a random track index that is different from `excludeIndex`.
 * Falls back to 0 when the track list has a single entry.
 */
function pickRandomIndex(length, excludeIndex) {
    if (length <= 1) return 0;
    let next = Math.floor(Math.random() * length);
    while (next === excludeIndex) {
        next = Math.floor(Math.random() * length);
    }
    return next;
}

export function BackgroundAudioProvider({ children }) {
    const audioRef = useRef(null);
    const currentTrackIndexRef = useRef(-1);

    const [isReady, setIsReady] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const hasUnlockedRef = useRef(false);

    // Manifesto music-ducking: remember the pre-duck volume to restore to, and whether a duck is active.
    const preDuckVolumeRef = useRef(DEFAULT_VOLUME);
    const isManifestoDuckedRef = useRef(false);

    // Load a track by index into the shared <audio> element without auto-starting.
    const loadTrack = useCallback((index) => {
        const audio = audioRef.current;
        if (!audio) return;
        const src = backgroundTracks[index];
        if (!src) return;
        audio.src = src;
        audio.load();
        currentTrackIndexRef.current = index;
        setCurrentTrackIndex(index);
    }, []);

    const playAudio = useCallback(() => {
        // Generative mode: start (first call) or unmute (later calls) the Tone.js
        // engine. start() is idempotent, so this safely covers both. Optimistic
        // isPlaying for instant UI; reverted only if Tone.start() rejects.
        if (IS_GENERATIVE) {
            // eslint-disable-next-line no-console
            console.log('[audio] gesture → playAudio (generative) → startSiteAudio');
            setIsPlaying(true);
            startSiteAudio()
                .then(() => setSiteAudioMuted(false))
                .catch((err) => {
                    // Surface the real reason instead of swallowing it — this is how
                    // a thrown node-construction / Tone.start() failure becomes visible.
                    // eslint-disable-next-line no-console
                    console.error('[audio] startSiteAudio FAILED:', err);
                    setIsPlaying(false);
                });
            return;
        }
        if (AUDIO_MODE === AUDIO_MODES.OFF) return;

        const audio = audioRef.current;
        if (!audio) return;
        // If no track has been chosen yet, pick one before attempting playback.
        if (currentTrackIndexRef.current === -1) {
            loadTrack(pickRandomIndex(backgroundTracks.length, -1));
        }
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === 'function') {
            playPromise
                .then(() => {
                    setIsPlaying(true);
                })
                .catch(() => {
                    // Autoplay or other playback errors — keep state false so the
                    // unlock layer can retry on a user gesture.
                    setIsPlaying(false);
                });
        } else {
            setIsPlaying(true);
        }
    }, [loadTrack]);

    const pauseAudio = useCallback(() => {
        // Generative mode: "pause" = ramp the engine to silence (mute), never tear
        // it down — so unmute resumes instantly without re-creating Tone nodes.
        if (IS_GENERATIVE) {
            setSiteAudioMuted(true);
            setIsPlaying(false);
            return;
        }
        if (AUDIO_MODE === AUDIO_MODES.OFF) {
            setIsPlaying(false);
            return;
        }

        const audio = audioRef.current;
        if (!audio) return;
        audio.pause();
        setIsPlaying(false);
    }, []);

    const toggleAudio = useCallback(() => {
        // An explicit toggle press counts as the unlock gesture — disarm the
        // body listener so a later click somewhere else does not override the
        // user's last toggle choice (e.g. resume music they just paused).
        if (!hasUnlockedRef.current) {
            hasUnlockedRef.current = true;
            setIsUnlocked(true);
        }
        if (isPlaying) {
            pauseAudio();
        } else {
            playAudio();
        }
    }, [isPlaying, pauseAudio, playAudio]);

    const playNextRandomTrack = useCallback(() => {
        // MP3-only concept — the generative engine has no discrete "tracks".
        if (!IS_MP3) return;
        const nextIndex = pickRandomIndex(
            backgroundTracks.length,
            currentTrackIndexRef.current
        );
        loadTrack(nextIndex);
        const audio = audioRef.current;
        if (!audio) return;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === 'function') {
            playPromise
                .then(() => setIsPlaying(true))
                .catch(() => setIsPlaying(false));
        } else {
            setIsPlaying(true);
        }
    }, [loadTrack]);

    // First-gesture unlock: browsers block playback until a user gesture.
    // A single pointerdown anywhere on the document triggers playback once,
    // then the listener removes itself. Clicks on a future audio-toggle UI
    // (marked with [data-audio-toggle]) are ignored here so the toggle owns
    // its own logic without double-firing through this path.
    useEffect(() => {
        if (hasUnlockedRef.current) return undefined;

        const handleFirstInteraction = (event) => {
            if (hasUnlockedRef.current) return;
            const target = event.target;
            if (
                target &&
                typeof target.closest === 'function' &&
                target.closest('[data-audio-toggle]')
            ) {
                return;
            }
            hasUnlockedRef.current = true;
            document.removeEventListener('pointerdown', handleFirstInteraction);
            setIsUnlocked(true);
            playAudio();
        };

        document.addEventListener('pointerdown', handleFirstInteraction);

        return () => {
            document.removeEventListener('pointerdown', handleFirstInteraction);
        };
    }, [playAudio]);

    // Create the single Audio element once and wire up its listeners.
    // MP3 mode only — in generative/off mode no <audio> element is created, so
    // no MP3 is ever requested or played.
    useEffect(() => {
        if (!IS_MP3) return undefined;
        const audio = new Audio();
        audio.volume = DEFAULT_VOLUME;
        audio.preload = 'auto';
        audioRef.current = audio;

        const handleEnded = () => {
            // When a track finishes, pick a new non-repeating random one.
            const nextIndex = pickRandomIndex(
                backgroundTracks.length,
                currentTrackIndexRef.current
            );
            audio.src = backgroundTracks[nextIndex];
            audio.load();
            currentTrackIndexRef.current = nextIndex;
            setCurrentTrackIndex(nextIndex);
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise
                    .then(() => setIsPlaying(true))
                    .catch(() => setIsPlaying(false));
            }
        };

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleCanPlay = () => setIsReady(true);
        const handleError = () => setIsPlaying(false);

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('canplaythrough', handleCanPlay);
        audio.addEventListener('error', handleError);

        // Preload an initial random track so playAudio() can fire immediately
        // when the user gesture unlock happens.
        const initialIndex = pickRandomIndex(backgroundTracks.length, -1);
        audio.src = backgroundTracks[initialIndex];
        audio.load();
        currentTrackIndexRef.current = initialIndex;
        setCurrentTrackIndex(initialIndex);

        return () => {
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('canplaythrough', handleCanPlay);
            audio.removeEventListener('error', handleError);
            audio.pause();
            audio.src = '';
            audioRef.current = null;
        };
    }, []);

    // ── Duck the background music during the hero manifesto ───────────────────
    // The manifesto ritual broadcasts its active state via the manifestoBackground
    // pub/sub (Home fires publishManifesto in both shimmer and legacy modes). While
    // active we fade the music VOLUME down so the spoken voice is clear, and fade it
    // back on release. We never pause — so the play/pause state the user chose is
    // preserved across the ritual (music that was playing keeps playing and comes
    // back up; music that was off stays off, the volume change being inaudible).
    // MP3 mode only — the generative bed is whisper-quiet and sits under the
    // (full-volume) manifesto voice without needing to duck.
    useEffect(() => {
        if (!IS_MP3) return undefined;
        const unsub = subscribeManifestoBackground({
            onManifesto: (active) => {
                const audio = audioRef.current;
                if (!audio) return;
                gsap.killTweensOf(audio);
                if (active) {
                    // Capture the pre-duck volume once — guarded so a repeat activation
                    // mid-fade can't latch the already-ducked value as the restore target.
                    if (!isManifestoDuckedRef.current) {
                        preDuckVolumeRef.current = audio.volume;
                        isManifestoDuckedRef.current = true;
                    }
                    gsap.to(audio, { volume: MANIFESTO_DUCK_VOLUME, duration: MANIFESTO_DUCK_FADE, ease: 'power2.out' });
                } else {
                    isManifestoDuckedRef.current = false;
                    gsap.to(audio, { volume: preDuckVolumeRef.current, duration: MANIFESTO_DUCK_FADE, ease: 'power2.inOut' });
                }
            },
        });
        return () => {
            unsub();
            if (audioRef.current) gsap.killTweensOf(audioRef.current);
        };
    }, []);

    // ── Generative interaction reactivity ─────────────────────────────────────
    // GENERATIVE mode only. Pure event mapping (no Tone.js here): existing pub/subs
    // + one delegated hover listener drive the engine's high-level interaction API.
    //   • homeSection (index 0..3) → section preset + a subtle transition sweep.
    //   • manifestoBackground.onHold (0..1 per frame, null on release) → hold
    //     start/progress/release. HomeHero publishes it for BOTH desktop pointer
    //     and mobile touch, so one subscription covers mobile (no duplicate logic).
    //   • manifestoBackground.onManifesto(true|false) → bloom / return to bed.
    //   • pointerover on interactive elements → a tiny hover impulse.
    // Reduced motion skips the transient events (hover/transition) to match the
    // existing hold gating in HomeHero; the ambient bed is unaffected.
    useEffect(() => {
        if (!IS_GENERATIVE) return undefined;
        setIsReady(true);

        // Apply whatever section Home already published — baseline only, no sweep.
        const current = getHomeSection();
        if (current !== null && current !== undefined && HOME_INDEX_TO_AUDIO_STATE[current]) {
            setSiteAudioSection(HOME_INDEX_TO_AUDIO_STATE[current]);
        }

        // Track the previous section index so transitions are direction-aware
        // (the engine maps from→to to a camera move: enter / travel / descend / close).
        let prevIndex = (current === null || current === undefined) ? null : current;
        const unsubSection = subscribeHomeSection((index) => {
            if (index === null || index === undefined) return; // home unmounted → keep last section
            const name = HOME_INDEX_TO_AUDIO_STATE[index];
            if (!name) return;
            setSiteAudioSection(name); // ramp the bed to the new preset
            if (!prefersReducedMotion() && prevIndex !== null && prevIndex !== index) {
                const fromName = HOME_INDEX_TO_AUDIO_STATE[prevIndex] || 'home';
                siteAudioSectionTransition(fromName, name); // directional, camera-aligned move
            }
            prevIndex = index;
        });

        let holding = false;
        const unsubManifesto = subscribeManifestoBackground({
            onHold: (progress) => {
                if (progress == null) {
                    if (holding) { siteAudioHoldRelease(); holding = false; }
                } else {
                    if (!holding) { siteAudioHoldStart(); holding = true; }
                    siteAudioHoldProgress(progress);
                }
            },
            onManifesto: (active) => {
                if (active) { siteAudioHoldComplete(); holding = false; } // bloom → manifesto bed
                else { siteAudioHoldRelease(); } // manifesto closed → return to the section bed
            },
        });

        // Interaction micro-sonification — committed navigation events published
        // by WorkSelection / WorkDetail / Demos via lib/interactionAudio. Gated
        // per-event on reduced motion (same convention as the section sweep above).
        const unsubInteraction = subscribeInteractionAudio({
            onProjectPreviewChange: (slug) => { if (!prefersReducedMotion()) siteAudioProjectPreviewChange(slug); },
            onProjectCanvasTransition: (fromSlug, toSlug) => { if (!prefersReducedMotion()) siteAudioProjectCanvasTransition(fromSlug, toSlug); },
            onProjectOpen: (slug) => { if (!prefersReducedMotion()) siteAudioProjectOpen(slug); },
            onProjectClose: (slug) => { if (!prefersReducedMotion()) siteAudioProjectClose(slug); },
            onLabNavigate: (direction) => { if (!prefersReducedMotion()) siteAudioLabNavigate(direction); },
        });

        // Hover delegation — one listener, no per-component wiring. The engine
        // self-throttles and self-mutes, so this stays cheap.
        const HOVER_SELECTOR = 'a, button, [role="button"], [data-audio-hover]';
        let lastHoverEl = null;
        const onPointerOver = (e) => {
            if (e.pointerType && e.pointerType !== 'mouse') return; // hover is a mouse concept, not touch taps
            const node = e.target;
            if (!node || typeof node.closest !== 'function') return;
            const el = node.closest(HOVER_SELECTOR);
            if (!el || el === lastHoverEl) return;
            lastHoverEl = el; // track even skipped elements so re-entry stays deduped
            // Elements that carry their own interaction sound (e.g. the PROJECTS
            // list rows → projectPreviewChange) opt out of the generic tick.
            if (el.closest('[data-audio-hover-skip]')) return;
            siteAudioHover();
        };
        const onPointerOut = (e) => {
            // Reset once the pointer leaves the tracked element so re-entry re-fires.
            if (lastHoverEl && (!e.relatedTarget || !lastHoverEl.contains(e.relatedTarget))) {
                lastHoverEl = null;
            }
        };
        const hoverEnabled = !prefersReducedMotion();
        if (hoverEnabled) {
            document.addEventListener('pointerover', onPointerOver);
            document.addEventListener('pointerout', onPointerOut);
        }

        return () => {
            unsubSection();
            unsubManifesto();
            unsubInteraction();
            if (hoverEnabled) {
                document.removeEventListener('pointerover', onPointerOver);
                document.removeEventListener('pointerout', onPointerOut);
            }
        };
    }, []);

    const value = useMemo(
        () => ({
            isReady,
            isInitialized: isReady,
            isPlaying,
            isUnlocked,
            hasUserInteracted: isUnlocked,
            currentTrackIndex,
            playAudio,
            pauseAudio,
            toggleAudio,
            playNextRandomTrack,
        }),
        [
            isReady,
            isPlaying,
            isUnlocked,
            currentTrackIndex,
            playAudio,
            pauseAudio,
            toggleAudio,
            playNextRandomTrack,
        ]
    );

    return (
        <BackgroundAudioContext.Provider value={value}>
            {children}
        </BackgroundAudioContext.Provider>
    );
}

export function useBackgroundAudio() {
    const ctx = useContext(BackgroundAudioContext);
    if (!ctx) {
        throw new Error(
            'useBackgroundAudio must be used within a BackgroundAudioProvider'
        );
    }
    return ctx;
}

export default BackgroundAudioProvider;
